import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useStore } from "react-redux";

import { UseFormReturnType } from "@mantine/form";

import isEqual from "lodash/isEqual";

import { EncounterContext } from "@/contexts/encounter";
import type { RootState } from "@/store";
import { selectAllActiveProcedures } from "@/store/slices/procedures";
import { ProceduresFormType, ProceduresFormTypeWithId } from "@/zod-schemas/procedures";

import { toProceduresFormValues } from "../components/EncounterProcedures/utils/procedure";

import useDrawerUnsavedChanges from "./useDrawerUnsavedChanges";

const LIST_KEY = "encounter-procedures-list";
const DRAWER_FORM_KEY = "encounter-procedures-drawer-form";

function sortProceduresById(list: ProceduresFormTypeWithId[]) {
  return [...list].sort((a, b) => a.id.localeCompare(b.id));
}

/** lodash isEqual treats `{ a: undefined }` and `{}` as different; strip undefined recursively for stable comparison. */
function stripUndefinedDeep(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) {
    const next = value.map(stripUndefinedDeep).filter((item) => item !== undefined);
    return next.length === 0 ? undefined : next;
  }
  const obj = value as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    const inner = obj[key];
    if (inner === undefined) continue;
    const normalized = stripUndefinedDeep(inner);
    if (normalized !== undefined) result[key] = normalized;
  }
  return Object.keys(result).length === 0 ? undefined : result;
}

function proceduresComparableForDiff(list: ProceduresFormTypeWithId[]) {
  return stripUndefinedDeep(sortProceduresById(list));
}

/**
 * Registers procedures list + CPT drawer form with encounter-level unsaved checks (navigation blocker).
 * Call `markProceduresListSaved` after loading from the server, after persisting questionnaire responses,
 * and after inline deletes that already sync to the server.
 * Call `markDrawerFormSaved` after `form.reset()` when closing the drawer or committing a row to Redux.
 */
function useEncounterProceduresUnsavedChanges(
  form: UseFormReturnType<ProceduresFormType>,
  targetCptCode: ProceduresFormTypeWithId | undefined,
  encounterId: string | undefined,
) {
  const { registerUnsavedCheck, unregisterUnsavedCheck } = useContext(EncounterContext);
  const store = useStore<RootState>();

  const listBaselineRef = useRef<ProceduresFormTypeWithId[] | null>(null);
  const encounterIdRef = useRef(encounterId);

  if (encounterIdRef.current !== encounterId) {
    encounterIdRef.current = encounterId;
    listBaselineRef.current = null;
  }

  const drawerServerValues = useMemo(() => toProceduresFormValues(targetCptCode), [targetCptCode]);

  const { markAsSaved: markDrawerFormSaved, markValuesAsSaved: markDrawerValuesSaved } =
    useDrawerUnsavedChanges(DRAWER_FORM_KEY, form, drawerServerValues);

  useLayoutEffect(() => {
    markDrawerValuesSaved(drawerServerValues);
  }, [drawerServerValues, markDrawerValuesSaved]);

  const markProceduresListSaved = useCallback(() => {
    listBaselineRef.current = structuredClone(selectAllActiveProcedures(store.getState()));
  }, [store]);

  useEffect(() => {
    registerUnsavedCheck?.(LIST_KEY, () => {
      if (listBaselineRef.current === null) return false;
      const current = selectAllActiveProcedures(store.getState());
      return !isEqual(
        proceduresComparableForDiff(current),
        proceduresComparableForDiff(listBaselineRef.current),
      );
    });
    return () => unregisterUnsavedCheck?.(LIST_KEY);
  }, [registerUnsavedCheck, unregisterUnsavedCheck, store]);

  return { markProceduresListSaved, markDrawerFormSaved };
}

export default useEncounterProceduresUnsavedChanges;
